import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { getRecordStatuses } from '../../api'
import { JsonRecordStatus, RecordStatus, RecordSubstatus } from '../../models'

interface FilterProps {
    updateFilters: (
        filterName: string,
        newValue: string,
        shouldDelete?: boolean,
        dependentFilterToClear?: string
    ) => void
    statusFilter?: (status: RecordStatus) => boolean
}

export const RecordStatusFilters = (props: FilterProps) => {
    const { updateFilters, statusFilter } = props

    const [searchParams, setSearchParams] = useSearchParams()
    const [recordStatuses, setRecordStatuses] = useState<RecordStatus[]>([])
    const [selectedParentStatus, setSelectedParentStatus] = useState<RecordStatus>()
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const urlStatus = searchParams.get('record_status_code')
    const urlSubstatus = searchParams.get('record_substatus_code')

    useEffect(() => {
        setIsLoading(true)
        setError(null)
        getRecordStatuses()
            .then(json => {
                let statuses = json.data.map((status: JsonRecordStatus) => new RecordStatus(status))

                // Apply optional filter
                if (statusFilter) {
                    statuses = statuses.filter(statusFilter)
                }

                setRecordStatuses(statuses)
            })
            .catch(error => {
                console.error('Failed to load record statuses:', error)
                setError('Failed to load record statuses. Please try refreshing the page.')
            })
            .finally(() => setIsLoading(false))
    }, [statusFilter])

    useEffect(() => {
        if (urlStatus && urlStatus !== '-1' && recordStatuses.length > 0) {
            setSelectedParentStatus(
                recordStatuses.find(status => status.code === +urlStatus)
            )
        } else if (!urlStatus || urlStatus === '-1') {
            setSelectedParentStatus(undefined)
        }
    }, [urlStatus, recordStatuses])

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value
        const nextSearchParams = new URLSearchParams(searchParams)

        if (value === '-1') {
            nextSearchParams.delete('record_status_code')
            nextSearchParams.delete('record_substatus_code')
        } else {
            nextSearchParams.set('record_status_code', value)
            nextSearchParams.delete('record_substatus_code')
        }

        if (nextSearchParams.toString() !== searchParams.toString()) {
            setSearchParams(nextSearchParams)
        }

        updateFilters(
            'record_status_code',
            value ?? '',
            value === '-1',
            'record_substatus_code'
        )
        setSelectedParentStatus(
            recordStatuses.find(status => status.code === +value)
        )
    }

    const handleSubstatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value
        const nextSearchParams = new URLSearchParams(searchParams)

        if (value === '-1') {
            nextSearchParams.delete('record_substatus_code')
        } else {
            nextSearchParams.set('record_substatus_code', value)
        }

        if (nextSearchParams.toString() !== searchParams.toString()) {
            setSearchParams(nextSearchParams)
        }

        updateFilters(
            'record_substatus_code',
            value ?? '',
            value === '-1'
        )
    }

    if (error) {
        return (
            <div className="row g-2">
                <div className="col-12">
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="row g-2">
            <div className="col-md-6">
                <label htmlFor='recordStatusFilter'>Record Status</label>
                <select
                    className='form-select'
                    id='recordStatusFilter'
                    aria-label='Filter by record status'
                    disabled={isLoading}
                    value={urlStatus || '-1'}
                    onChange={handleStatusChange}>
                    <option value="-1">
                        {isLoading ? 'Loading...' : 'All'}
                    </option>
                    {recordStatuses.map((status: RecordStatus) => (
                        <option key={status.code} value={status.code}>
                            {status.name}
                        </option>
                    ))}
                </select>
            </div>
            {selectedParentStatus?.substatuses && selectedParentStatus.substatuses.length > 0 && (
                <div className="col-md-6">
                    <label htmlFor='recordSubstatusFilter'>Record Sub Status</label>
                    <select
                        className='form-select'
                        id='recordSubstatusFilter'
                        aria-label='Filter by record sub status'
                        disabled={isLoading}
                        value={urlSubstatus || '-1'}
                        onChange={handleSubstatusChange}>
                        <option value="-1">
                            All
                        </option>
                        {selectedParentStatus.substatuses.map((substatus: RecordSubstatus) => (
                            <option key={substatus.code} value={substatus.code}>
                                {substatus.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    )
}
